import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { filtersToQuery, parseFiltersFromQuery } from "../utils/filterQuery";
/**
 * 카테고리 페이지의 URL 기반 상태를 관리하는 공통 훅
 * - 에디션/시스템 카테고리 페이지에서 공유
 * - URL 쿼리 파싱 (categoryId, 필터, 정렬, 페이지)
 * - 필터/정렬/페이지 상태 및 URL 동기화
 */ export const useCategoryUrlState = function() {
    let { reservedParams =[
        "id",
        "page",
        "sort"
    ] , defaultSort ="인기순"  } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    const router = useRouter();
    const { id: queryCategoryId , page: queryPage , sort: querySort , ...restQuery } = router.query;
    const selectedCategoryId = typeof queryCategoryId === "string" ? queryCategoryId : undefined;
    // 예약된 파라미터를 제외한 필터 쿼리 추출
    const filterQuery = useMemo(()=>{
        const query = {
            ...restQuery
        };
        for (const key of reservedParams){
            delete query[key];
        }
        return query;
    }, [
        restQuery,
        reservedParams
    ]);
    // URL에서 초기 필터 상태 파싱
    const initialFilters = useMemo(()=>router.isReady ? parseFiltersFromQuery(filterQuery, reservedParams) : {}, [
        router.isReady
    ]);
    // 상태
    const [currentPage, setCurrentPage] = useState(()=>{
        const page = typeof queryPage === "string" ? parseInt(queryPage, 10) : 1;
        return isNaN(page) ? 1 : page;
    });
    const [selectedSort, setSelectedSort] = useState(()=>typeof querySort === "string" ? querySort : defaultSort);
    const [selectedFilters, setSelectedFilters] = useState(initialFilters);
    // URL 파라미터에서 초기값 설정
    useEffect(()=>{
        if (!router.isReady) return;
        const filters = parseFiltersFromQuery(filterQuery, reservedParams);
        setSelectedFilters(filters);
        const page = typeof queryPage === "string" ? parseInt(queryPage, 10) : 1;
        setCurrentPage(isNaN(page) ? 1 : page);
        const sort = typeof querySort === "string" ? querySort : defaultSort;
        setSelectedSort(sort);
    }, [
        router.isReady,
        selectedCategoryId
    ]);
    // URL 업데이트 함수 (shallow routing)
    const updateUrlParams = useCallback((params)=>{
        const newQuery = {
            ...router.query
        };
        Object.entries(params).forEach((param)=>{
            let [key, value] = param;
            if (value === undefined || value === "" || value === 1) {
                delete newQuery[key];
            } else {
                newQuery[key] = String(value);
            }
        });
        router.replace({
            pathname: router.pathname,
            query: newQuery
        }, undefined, {
            shallow: true
        });
    }, [
        router
    ]);
    // 핸들러
    const handleSortChange = useCallback((sort)=>{
        setSelectedSort(sort);
        setCurrentPage(1);
        updateUrlParams({
            sort: sort === defaultSort ? undefined : sort,
            page: undefined
        });
    }, [
        updateUrlParams,
        defaultSort
    ]);
    const handleFilterChange = useCallback((key, values)=>{
        const newFilters = {
            ...selectedFilters,
            [key]: values
        };
        setSelectedFilters(newFilters);
        setCurrentPage(1);
        const filterParams = filtersToQuery(newFilters);
        updateUrlParams({
            ...filterParams,
            page: undefined
        });
    }, [
        selectedFilters,
        updateUrlParams
    ]);
    const handleFilterReset = useCallback(()=>{
        const resetParams = {};
        Object.keys(selectedFilters).forEach((key)=>{
            resetParams[key] = undefined;
        });
        setSelectedFilters({});
        setCurrentPage(1);
        updateUrlParams({
            ...resetParams,
            page: undefined
        });
    }, [
        selectedFilters,
        updateUrlParams
    ]);
    const handlePageChange = useCallback((page)=>{
        setCurrentPage(page);
        updateUrlParams({
            page: page === 1 ? undefined : page
        });
    }, [
        updateUrlParams
    ]);
    /** 카테고리/과목 전환 시 필터/정렬/페이지 상태 초기화 */ const resetFiltersAndSort = useCallback(()=>{
        setCurrentPage(1);
        setSelectedFilters({});
        setSelectedSort(defaultSort);
    }, [
        defaultSort
    ]);
    return {
        // URL에서 파싱된 값
        selectedCategoryId,
        // 상태
        currentPage,
        selectedSort,
        selectedFilters,
        // 핸들러
        handleSortChange,
        handleFilterChange,
        handleFilterReset,
        handlePageChange,
        resetFiltersAndSort
    };
};
